import { LOCALES, type Locale } from '@aysar/contracts';
import { type FieldErrors } from 'react-hook-form';

import { type FieldDef } from '../types/dynamic-form.types';

/** The object every translatable field lives under, matching `makeTranslationsSchema`. */
export const TRANSLATIONS_KEY = 'translations';

/** Where a translatable field's value lives in the form: `translations.ar.name`. */
export function localizedPath(name: string, locale: Locale): string {
  return `${TRANSLATIONS_KEY}.${locale}.${name}`;
}

/**
 * Every form path a field set occupies — a translatable field contributing one
 * per locale.
 *
 * This is what tells `mapServerErrors` whether `translations.en.name` is a field
 * this form actually renders. A server error naming a path no control owns must
 * be shown at form level, or it is announced to nobody.
 */
export function fieldPaths(fields: readonly FieldDef[]): string[] {
  return fields.flatMap((field) =>
    field.isTranslatable === true
      ? LOCALES.map((locale) => localizedPath(field.name, locale))
      : [field.name],
  );
}

/** A path a host may point the operator at, and the tab it lives on. */
export interface FocusablePath {
  path: string;
  /** `null` for a field that has no locale of its own. */
  locale: Locale | null;
}

/**
 * The form's paths in reading order, **restricted to ones a focus can land on**.
 *
 * Two exclusions carry it. An `object` group renders a `<fieldset>` and no
 * control of its own, so `setFocus` on the group's path finds nothing and
 * silently does nothing — and because a group is listed BEFORE its properties,
 * a naive scan picks exactly that dead path and leaves the operator nowhere.
 * A translatable field contributes one entry per locale, because its control
 * only exists while its tab is open.
 */
export function focusablePaths(fields: readonly FieldDef[]): FocusablePath[] {
  return (
    fields
      .filter((field) => field.kind !== 'object')
      // Annotated: without it the two arms infer as two different array types and
      // `flatMap`'s overload resolution picks the "one item OR an array" signature.
      .flatMap<FocusablePath>((field) =>
        field.isTranslatable === true
          ? LOCALES.map((locale) => ({ path: localizedPath(field.name, locale), locale }))
          : [{ path: field.name, locale: null }],
      )
  );
}

/**
 * The locales whose tab contains at least one invalid field.
 *
 * The reason `handleSubmit` is given an `onInvalid` handler at all: a required
 * English name fails validation while the Arabic tab is on screen, and without
 * this the operator sees a Save button that does nothing, with no error visible
 * anywhere — the form is silently broken and the only clue is in a panel they
 * are not looking at.
 */
export function localesWithErrors(errors: FieldErrors): Locale[] {
  const translations = errors[TRANSLATIONS_KEY];

  if (translations === undefined || translations === null || typeof translations !== 'object') {
    return [];
  }

  const tree = translations as Record<string, unknown>;

  return LOCALES.filter((locale) => {
    const branch = tree[locale];
    return branch !== undefined && branch !== null && Object.keys(branch).length > 0;
  });
}

/**
 * The tab to open when a submit fails: the first locale with an error, or
 * `null` when the failure is outside the translations block entirely (a bad
 * key, say) and switching tabs would only hide the real problem.
 */
export function firstInvalidLocale(errors: FieldErrors): Locale | null {
  return localesWithErrors(errors)[0] ?? null;
}
